import { resolvePLACalculatedAttributes } from "./PLAExpression";
import { placeKeys, samePlace } from "./planitPlaceMatch";
import {
   getPLAHierarchicalRelations,
   getPLAValueTypePostfix,
   isPLANumberValueType,
   PLAElementRecord,
   PLAProject,
   PLARelationRecord,
   PLAValueType,
} from "./PLAState";

export type PlanitDocumentKind = "lokalplan" | "arealskema";
export type PlanitConfidence = "high" | "medium" | "low";
export type PLALLMCheckStatus = "ok" | "mismatch" | "missing_in_pla" | "missing_in_document";
export type PLALLMCheckVersionChange = "added" | "removed" | "changed" | "unchanged";

export interface PlanitVariable {
   id: string;
   name: string;
   elementTypeId?: string;
   elementTypeName?: string;
   unit?: string;
   description?: string;
}

export interface PlanitElementRef {
   name: string;
   elementTypeId: string;
   elementTypeName: string;
   parentName?: string;
   parentTypeName?: string;
}

export interface PlanitElements {
   items: PlanitElementRef[];
}

export interface PlanitAppliesTo {
   elementTypeId?: string | null;
   elementTypeName?: string | null;
   elementNames?: string[];
   delomraader?: string[];
   byggefelter?: string[];
   anvendelse?: string | null;
}

export interface PlanitFinding {
   variableId: string;
   appliesTo: PlanitAppliesTo;
   scope?: string | null;
   value: number | null;
   unit: string | null;
   formula: string | null;
   conditions: string | null;
   quote: string;
   page: number | null;
   paragraph: string | null;
   confidence: PlanitConfidence;
   sheet?: string | null;
   cell?: string | null;
}

export interface PlanitExtractResult {
   findings: PlanitFinding[];
   otherRelevant: { topic: string; quote: string; page: number | null }[];
   warnings: string[];
   model: string;
   pages: number;
   chunks?: number;
   llm_duration_ms?: number;
}

export interface PLALLMCheckStoredRun {
   timestamp: string;
   filename: string;
   kind: PlanitDocumentKind;
   result: PlanitExtractResult;
}

export interface PLALLMCheckHistory {
   current?: PLALLMCheckStoredRun;
   previous?: PLALLMCheckStoredRun;
}

export interface PLALLMCheckApplyChange {
   elementId: string;
   attributeId: string;
   value: number;
   valueType: PLAValueType;
}

export interface PLALLMCompareRow {
   id: string;
   variableId: string;
   variableName: string;
   attributeId?: string;
   elementId?: string;
   elementLabel: string;
   plaValue: number | string | null;
   documentValue: number | string | null;
   unit: string;
   status: PLALLMCheckStatus;
   quote: string;
   page: number | null;
   paragraph: string | null;
   formula: string | null;
   confidence: PlanitConfidence | null;
   finding: PlanitFinding | null;
   appliable: boolean;
}

export interface PLALLMVersionRow {
   id: string;
   change: PLALLMCheckVersionChange;
   variableId: string;
   elementLabel: string;
   previousValue: number | string | null;
   currentValue: number | string | null;
   quote: string;
   page: number | null;
}

const STORAGE_PREFIX = "pla-llm-check";

export function collectPlanitVariables(project: PLAProject): PlanitVariable[] {
   const variables: PlanitVariable[] = [];
   for (const [elementTypeId, elementType] of Object.entries(project.elementTypes || {})) {
      for (const [attributeId, definition] of Object.entries(elementType.attributes || {})) {
         if (definition.type !== "constant" || !isPLANumberValueType(definition.valueType)) continue;
         variables.push({
            id: attributeId,
            name: definition.name || attributeId,
            elementTypeId,
            elementTypeName: elementType.name || elementTypeId,
            unit: getPLAValueTypePostfix(definition.valueType).trim() || undefined,
            description: definition.description,
         });
      }
   }
   return variables;
}

export function collectPlanitElements(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]): PlanitElements {
   const parentByChildId = new Map<string, PLAElementRecord>();
   const byId = new Map(elements.map((element) => [element.id, element]));
   getPLAHierarchicalRelations(project, relations).forEach((relation) => {
      const parent = byId.get(relation.fromId);
      if (parent) parentByChildId.set(relation.toId, parent);
   });
   return {
      items: elements.flatMap((element) => {
         const name = (element.name || "").trim();
         if (!name) return [];
         const parent = parentByChildId.get(element.id);
         const elementTypeName = project.elementTypes[element.elementType]?.name || element.elementType;
         return [{
            name,
            elementTypeId: element.elementType,
            elementTypeName,
            parentName: parent?.name,
            parentTypeName: parent ? (project.elementTypes[parent.elementType]?.name || parent.elementType) : undefined,
         }];
      }),
   };
}

export function loadPlanitCheckHistory(projectId: string, kind: PlanitDocumentKind): PLALLMCheckHistory {
   try {
      const raw = localStorage.getItem(storageKey(projectId, kind));
      if (!raw) return {};
      const parsed = JSON.parse(raw) as PLALLMCheckHistory;
      return parsed && typeof parsed === "object" ? parsed : {};
   } catch {
      return {};
   }
}

export function savePlanitCheckRun(projectId: string, kind: PlanitDocumentKind, run: PLALLMCheckStoredRun): PLALLMCheckHistory {
   const history = loadPlanitCheckHistory(projectId, kind);
   const next: PLALLMCheckHistory = {
      previous: history.current,
      current: run,
   };
   localStorage.setItem(storageKey(projectId, kind), JSON.stringify(next));
   return next;
}

export function compareFindingsToPLA(
   project: PLAProject,
   elements: PLAElementRecord[],
   relations: PLARelationRecord[],
   result: PlanitExtractResult
): PLALLMCompareRow[] {
   const variables = collectPlanitVariables(project);
   const rows: PLALLMCompareRow[] = [];

   (result.findings || []).forEach((finding, findingIndex) => {
      const variable = matchVariable(variables, project, finding);
      const namedElements = variable ? linkFindingElements(project, elements, finding, variable) : [];
      if (!variable || !namedElements.length) {
         rows.push(makeRow({
            id: `finding-${findingIndex}`,
            variable,
            finding,
            elementLabel: documentLabel(finding),
            plaValue: null,
            status: "missing_in_pla",
            appliable: false,
         }));
         return;
      }

      namedElements.forEach((element, matchIndex) => {
         const attributeId = findExactAttributeId(project, element, variable);
         const definition = attributeId ? project.elementTypes[element.elementType]?.attributes?.[attributeId] : undefined;
         const plaValue = attributeId
            ? getNumericAttributeValue(project, element, elements, relations, attributeId)
            : null;
         const documentValue = finding.value;
         const status: PLALLMCheckStatus = !attributeId
            ? "missing_in_pla"
            : plaValue === null
               ? "missing_in_pla"
               : documentValue === null
                  ? "ok"
                  : numbersMatch(plaValue, documentValue) ? "ok" : "mismatch";
         const typeName = project.elementTypes[element.elementType]?.name || element.elementType;
         rows.push(makeRow({
            id: `finding-${findingIndex}-${matchIndex}`,
            variable,
            finding,
            elementId: element.id,
            elementLabel: `${typeName}: ${element.name}`,
            attributeId,
            plaValue,
            status,
            appliable: Boolean(attributeId && definition?.type !== "expression" && documentValue !== null),
         }));
      });
   });

   return rows;
}

export function diffPlanitRuns(previous: PlanitExtractResult | undefined, current: PlanitExtractResult): PLALLMVersionRow[] {
   const prevMap = new Map((previous?.findings || []).map((finding, index) => [findingIdentity(finding), { finding, index }]));
   const currMap = new Map((current.findings || []).map((finding, index) => [findingIdentity(finding), { finding, index }]));
   const keys = uniquePreserve([...prevMap.keys(), ...currMap.keys()]);
   return keys.map((key) => {
      const prev = prevMap.get(key)?.finding;
      const curr = currMap.get(key)?.finding;
      const change: PLALLMCheckVersionChange = !prev ? "added" : !curr ? "removed" : valuesDiffer(prev, curr) ? "changed" : "unchanged";
      const finding = curr || prev!;
      return {
         id: key,
         change,
         variableId: finding.variableId,
         elementLabel: formatAppliesTo(finding.appliesTo),
         previousValue: prev ? displayFindingValue(prev) : null,
         currentValue: curr ? displayFindingValue(curr) : null,
         quote: (curr || prev)?.quote || "",
         page: (curr || prev)?.page ?? null,
      };
   }).filter((row) => row.change !== "unchanged");
}

function makeRow(input: {
   id: string;
   variable: PlanitVariable | undefined;
   finding: PlanitFinding | null;
   elementId?: string;
   elementLabel: string;
   attributeId?: string;
   plaValue: number | string | null;
   status: PLALLMCheckStatus;
   appliable: boolean;
}): PLALLMCompareRow {
   return {
      id: input.id,
      variableId: input.variable?.id || input.finding?.variableId || "",
      variableName: variableLabel(input.variable) || input.finding?.variableId || "",
      attributeId: input.attributeId,
      elementId: input.elementId,
      elementLabel: input.elementLabel,
      plaValue: input.plaValue,
      documentValue: input.finding ? displayFindingValue(input.finding) : null,
      unit: input.finding?.unit || input.variable?.unit || "",
      status: input.status,
      quote: input.finding?.quote || "",
      page: input.finding?.page ?? null,
      paragraph: input.finding?.paragraph ?? null,
      formula: input.finding?.formula ?? null,
      confidence: input.finding?.confidence ?? null,
      finding: input.finding,
      appliable: input.appliable,
   };
}

function matchVariable(variables: PlanitVariable[], project: PLAProject, finding: PlanitFinding): PlanitVariable | undefined {
   const variableKey = normalizeKey(finding.variableId);
   const typeKey = normalizeKey(finding.appliesTo?.elementTypeId || finding.appliesTo?.elementTypeName);
   const exact = variables.filter((variable) => normalizeKey(variable.id) === variableKey || normalizeKey(variable.name) === variableKey);
   const typed = typeKey
      ? exact.filter((variable) => normalizeKey(variable.elementTypeId) === typeKey || normalizeKey(variable.elementTypeName) === typeKey)
      : [];
   if (typed.length === 1) return typed[0];
   if (typed.length > 1) return undefined;
   if (!typeKey && exact.length === 1) return exact[0];
   if (!typeKey && exact.length > 1) return undefined;
   const found: PlanitVariable[] = [];
   for (const [elementTypeId, elementType] of Object.entries(project.elementTypes || {})) {
      if (typeKey && normalizeKey(elementTypeId) !== typeKey && normalizeKey(elementType.name) !== typeKey) continue;
      for (const [attributeId, definition] of Object.entries(elementType.attributes || {})) {
         if (normalizeKey(attributeId) === variableKey || normalizeKey(definition.name) === variableKey) {
            found.push({
               id: attributeId,
               name: definition.name || attributeId,
               elementTypeId,
               elementTypeName: elementType.name || elementTypeId,
               unit: getPLAValueTypePostfix(definition.valueType).trim() || undefined,
            });
         }
      }
   }
   return found.length === 1 ? found[0] : undefined;
}

function linkFindingElements(project: PLAProject, elements: PLAElementRecord[], finding: PlanitFinding, variable: PlanitVariable): PLAElementRecord[] {
   const evidence = `${finding.scope || ""} ${finding.quote || ""}`.trim();
   const documentKeys = placeKeys(evidence, finding.value);
   const named = matchNamedElements(project, elements, finding, variable);
   if (!documentKeys.size) return named;
   const agreed = named.filter((element) => samePlace(evidence, element.name, finding.value));
   if (agreed.length) return agreed;
   return elementsOfVariableType(project, elements, variable).filter((element) => samePlace(evidence, element.name, finding.value));
}

function elementsOfVariableType(project: PLAProject, elements: PLAElementRecord[], variable: PlanitVariable): PLAElementRecord[] {
   const typeKey = normalizeKey(variable.elementTypeId);
   const typeNameKey = normalizeKey(variable.elementTypeName);
   if (!typeKey && !typeNameKey) return [];
   return elements.filter((element) => {
      return normalizeKey(element.elementType) === typeKey || normalizeKey(project.elementTypes[element.elementType]?.name) === typeNameKey;
   });
}

function documentLabel(finding: PlanitFinding): string {
   const scope = finding.scope?.trim();
   if (scope) return scope;
   const quote = finding.quote?.trim();
   if (quote) return quote.length > 90 ? `${quote.slice(0, 87)}…` : quote;
   return formatAppliesTo(finding.appliesTo);
}

function matchNamedElements(project: PLAProject, elements: PLAElementRecord[], finding: PlanitFinding, variable: PlanitVariable): PLAElementRecord[] {
   const names = requestedElementNames(finding);
   if (!names.length || !variable.elementTypeId) return [];
   const typeKey = normalizeKey(variable.elementTypeId);
   const typeNameKey = normalizeKey(variable.elementTypeName);
   const nameKeys = new Set(names.map(normalizeKey));
   return elements.filter((element) => {
      const sameType = normalizeKey(element.elementType) === typeKey || normalizeKey(project.elementTypes[element.elementType]?.name) === typeNameKey;
      return sameType && nameKeys.has(normalizeKey(element.name));
   });
}

function requestedElementNames(finding: PlanitFinding): string[] {
   const named = finding.appliesTo?.elementNames || [];
   if (named.length) return named;
   return [
      ...(finding.appliesTo?.delomraader || []),
      ...(finding.appliesTo?.byggefelter || []),
      finding.appliesTo?.anvendelse || "",
   ].filter(Boolean);
}

function findExactAttributeId(project: PLAProject, element: PLAElementRecord, variable: PlanitVariable): string | undefined {
   const definitions = project.elementTypes[element.elementType]?.attributes || {};
   if (definitions[variable.id]) return variable.id;
   const nameKey = normalizeKey(variable.name);
   return Object.entries(definitions).find(([attributeId, definition]) => normalizeKey(attributeId) === nameKey || normalizeKey(definition.name) === nameKey)?.[0];
}

function getNumericAttributeValue(project: PLAProject, element: PLAElementRecord, elements: PLAElementRecord[], relations: PLARelationRecord[], attributeId: string): number | null {
   const definition = project.elementTypes[element.elementType]?.attributes?.[attributeId];
   let value: unknown;
   if (definition?.type === "expression") {
      value = resolvePLACalculatedAttributes(element, project, elements, relations, { includeErrors: true })[attributeId];
   } else {
      const attribute = element.attributes[attributeId];
      value = attribute?.mode === "constant" ? attribute.value : undefined;
   }
   const numeric = typeof value === "number" ? value : Number(value);
   return Number.isFinite(numeric) ? numeric : null;
}

function numbersMatch(a: number, b: number): boolean {
   const diff = Math.abs(a - b);
   return diff < 0.05 || diff / Math.max(Math.abs(a), Math.abs(b), 1) < 0.002;
}

function normalizeKey(value: string | null | undefined): string {
   return (value || "").trim().toLowerCase().replace(/[\s_-]+/g, "");
}

function variableLabel(variable: PlanitVariable | undefined): string {
   if (!variable) return "";
   return variable.elementTypeName ? `${variable.elementTypeName}: ${variable.name}` : variable.name;
}

function formatAppliesTo(appliesTo: PlanitAppliesTo | undefined): string {
   const names = appliesTo?.elementNames || [];
   if (appliesTo?.elementTypeName || appliesTo?.elementTypeId || names.length) {
      return [appliesTo?.elementTypeName || appliesTo?.elementTypeId || "", names.join(", ")].filter(Boolean).join(": ") || "—";
   }
   const parts = [
      ...(appliesTo?.delomraader || []),
      ...(appliesTo?.byggefelter || []),
      appliesTo?.anvendelse || "",
   ].filter(Boolean);
   return parts.join(" / ") || "—";
}

function findingIdentity(finding: PlanitFinding): string {
   return JSON.stringify({
      variableId: normalizeKey(finding.variableId),
      scope: normalizeKey(finding.scope || finding.quote),
      elementTypeId: normalizeKey(finding.appliesTo?.elementTypeId || finding.appliesTo?.elementTypeName),
      elementNames: [...(finding.appliesTo?.elementNames || [])].map(normalizeKey).sort(),
      delomraader: [...(finding.appliesTo?.delomraader || [])].map(normalizeKey).sort(),
      byggefelter: [...(finding.appliesTo?.byggefelter || [])].map(normalizeKey).sort(),
      anvendelse: normalizeKey(finding.appliesTo?.anvendelse || ""),
      conditions: normalizeKey(finding.conditions || ""),
   });
}

function valuesDiffer(a: PlanitFinding, b: PlanitFinding): boolean {
   if (a.value !== b.value) return true;
   return (a.formula || "") !== (b.formula || "");
}

function displayFindingValue(finding: PlanitFinding): number | string | null {
   if (finding.value !== null && finding.value !== undefined) return finding.value;
   return finding.formula;
}

function uniquePreserve(values: string[]): string[] {
   const seen = new Set<string>();
   return values.filter((value) => {
      const key = normalizeKey(value);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
   });
}

function storageKey(projectId: string, kind: PlanitDocumentKind): string {
   return `${STORAGE_PREFIX}-${projectId}-${kind}`;
}
